import { SALES_ESTIMATE_SECTION_KEYS } from '@protoss/shared';
import { Button, NumberInput, Tabs, TabsContent, Tooltip } from '@protoss/ui';
import * as React from 'react';

/**
 * Structured read/edit editor for a resource-plan contract (a "Resource Model"
 * profile). Mirrors RateCardPane's read-vs-edit toggle and single-onChange
 * pattern: every mutation produces the whole next config so the parent's
 * dirty-diff stays a trivial JSON compare.
 *
 * The editor is split across four Radix tabs (Phases · Roles · Effort
 * Distribution · Settings) so each concern gets the full pane width. Layout
 * differs by `distribution_model`:
 *  - flat_percentage: each phase gets a flat % of total (phase_percentages);
 *    roles get a flat % (role_percentages).
 *  - size_tiered: each phase's hours are "system-calculated" by attaching
 *    estimate sections (phase_section_map.section_keys); roles get a % per
 *    project size (role_allocations[size]).
 * The Effort Distribution tab holds the per-role×per-phase weight matrix; the
 * Settings tab holds velocity and the timeline/Gantt visibility toggles.
 */

type Phase = {
  key: string;
  label: string;
  order: number;
  display_group?: string;
  display_group_label?: string;
  milestone_label?: string;
  depends_on?: string[];
};
type RoleRef = { id: string; label: string };

export interface ResourceModelPaneProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  draftConfig: any;
  isEditing: boolean;
  roleList: RoleRef[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (next: any) => void;
}

const RM_TABS = [
  { value: 'settings', label: 'Settings' },
  { value: 'phases', label: 'Phases' },
  { value: 'roles', label: 'Roles' },
  { value: 'effort', label: 'Effort Distribution' },
] as const;

const toPct = (frac: number | undefined): number => Math.round((frac ?? 0) * 100);
const fromPct = (pct: number): number => (Number.isFinite(pct) ? pct / 100 : 0);
const labelize = (k: string): string =>
  k.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Normalize a phase name into a stable internal key (e.g. "NetSuite Build" → "netsuite_build"). */
const slugify = (label: string): string =>
  label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

/** Ensure a derived key is unique among `taken`, appending _2, _3… on collision. */
const uniqueKey = (base: string, taken: string[]): string => {
  const b = base || 'phase';
  if (!taken.includes(b)) return b;
  let n = 2;
  while (taken.includes(`${b}_${n}`)) n += 1;
  return `${b}_${n}`;
};

function sumPct(values: Array<number | undefined>): number {
  return Math.round(values.reduce<number>((s, v) => s + (v ?? 0), 0) * 100);
}

export function ResourceModelPane({
  draftConfig,
  isEditing,
  roleList,
  onChange,
}: ResourceModelPaneProps) {
  const [tab, setTab] = React.useState<string>('settings');
  const [dragOverIdx, setDragOverIdx] = React.useState<number | null>(null);
  const dragIndexRef = React.useRef<number | null>(null);

  const model: 'size_tiered' | 'flat_percentage' | 'ai' =
    draftConfig?.distribution_model ?? 'size_tiered';
  const phases: Phase[] = React.useMemo(
    () => [...(draftConfig?.phases ?? [])].sort((a: Phase, b: Phase) => a.order - b.order),
    [draftConfig]
  );

  // Roles: prefer the rate-card-derived list, fall back to ids already in the config.
  const roles: RoleRef[] = React.useMemo(() => {
    if (roleList.length > 0) return roleList;
    const ids = new Set<string>();
    Object.keys(draftConfig?.phase_weights ?? {}).forEach((r) => ids.add(r));
    Object.keys(draftConfig?.role_percentages ?? {}).forEach((r) => ids.add(r));
    Object.values(draftConfig?.role_allocations ?? {}).forEach((m) =>
      Object.keys(m as Record<string, number>).forEach((r) => ids.add(r))
    );
    return [...ids].map((id) => ({ id, label: id }));
  }, [roleList, draftConfig]);

  const sizes: string[] = React.useMemo(
    () => (draftConfig?.project_size_thresholds ?? []).map((t: { size: string }) => t.size),
    [draftConfig]
  );

  // Section → role direct-assignment overrides (size_tiered). Each entry routes
  // its sections' hours straight to a role, excluding it from the % allocation.
  const roleOverrides: Array<{ role_id: string; section_keys: string[] }> = React.useMemo(
    () => draftConfig?.role_section_overrides ?? [],
    [draftConfig]
  );
  const overrideRoleIds = React.useMemo(
    () => new Set(roleOverrides.map((o) => o.role_id)),
    [roleOverrides]
  );
  const overrideSectionsForRole = (roleId: string): string[] =>
    roleOverrides.filter((o) => o.role_id === roleId).flatMap((o) => o.section_keys);

  // ── Mutation helpers (immutable) ────────────────────────────────────────────
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const patch = (next: Record<string, any>) => onChange({ ...draftConfig, ...next });

  const setPhases = (nextPhases: Phase[]) => patch({ phases: nextPhases });

  const renamePhaseKey = (oldKey: string, newKey: string) => {
    if (!newKey || newKey === oldKey) {
      setPhases(phases.map((p) => (p.key === oldKey ? { ...p, key: newKey } : p)));
      return;
    }
    const next = { ...draftConfig };
    next.phases = phases.map((p) =>
      p.key === oldKey
        ? { ...p, key: newKey, depends_on: p.depends_on?.map((d) => (d === oldKey ? newKey : d)) }
        : { ...p, depends_on: p.depends_on?.map((d) => (d === oldKey ? newKey : d)) }
    );
    if (next.phase_percentages) {
      const pp = { ...next.phase_percentages };
      if (oldKey in pp) {
        pp[newKey] = pp[oldKey];
        delete pp[oldKey];
      }
      next.phase_percentages = pp;
    }
    if (Array.isArray(next.phase_section_map)) {
      next.phase_section_map = next.phase_section_map.map((s: { phase_key: string }) =>
        s.phase_key === oldKey ? { ...s, phase_key: newKey } : s
      );
    }
    if (next.phase_weights) {
      const pw: Record<string, Record<string, number>> = {};
      for (const [role, w] of Object.entries(
        next.phase_weights as Record<string, Record<string, number>>
      )) {
        const row = { ...w };
        if (oldKey in row) {
          row[newKey] = row[oldKey]!;
          delete row[oldKey];
        }
        pw[role] = row;
      }
      next.phase_weights = pw;
    }
    onChange(next);
  };

  // Phase label edits live (key stays stable so the input keeps focus); the
  // internal key is re-derived from the name on blur (and cascaded everywhere).
  const updatePhase = (key: string, field: 'label', value: string) =>
    setPhases(phases.map((p) => (p.key === key ? { ...p, [field]: value } : p)));

  const syncPhaseKey = (oldKey: string) => {
    const phase = phases.find((p) => p.key === oldKey);
    if (!phase) return;
    const newKey = uniqueKey(
      slugify(phase.label),
      phases.filter((p) => p.key !== oldKey).map((p) => p.key)
    );
    if (newKey !== oldKey) renamePhaseKey(oldKey, newKey);
  };

  const reorderPhases = (next: Phase[]) => setPhases(next.map((p, i) => ({ ...p, order: i })));

  const handlePhaseDragStart = (idx: number) => {
    dragIndexRef.current = idx;
  };
  const handlePhaseDragOver = (idx: number) => {
    if (dragIndexRef.current !== null && dragIndexRef.current !== idx) setDragOverIdx(idx);
  };
  const handlePhaseDragLeave = () => setDragOverIdx(null);
  const handlePhaseDrop = (targetIdx: number) => {
    const sourceIdx = dragIndexRef.current;
    dragIndexRef.current = null;
    setDragOverIdx(null);
    if (sourceIdx === null || sourceIdx === targetIdx) return;
    const reordered = [...phases];
    const [moved] = reordered.splice(sourceIdx, 1);
    if (!moved) return;
    reordered.splice(targetIdx, 0, moved);
    reorderPhases(reordered);
  };

  const addPhase = () => {
    const label = 'New phase';
    const key = uniqueKey(
      slugify(label),
      phases.map((p) => p.key)
    );
    setPhases([...phases, { key, label, order: phases.length }]);
  };

  const removePhase = (key: string) => {
    const next = { ...draftConfig };
    next.phases = phases.filter((p) => p.key !== key).map((p, i) => ({ ...p, order: i }));
    if (next.phase_percentages) {
      const pp = { ...next.phase_percentages };
      delete pp[key];
      next.phase_percentages = pp;
    }
    if (Array.isArray(next.phase_section_map)) {
      next.phase_section_map = next.phase_section_map.filter(
        (s: { phase_key: string }) => s.phase_key !== key
      );
    }
    if (next.phase_weights) {
      const pw: Record<string, Record<string, number>> = {};
      for (const [role, w] of Object.entries(
        next.phase_weights as Record<string, Record<string, number>>
      )) {
        const row = { ...w };
        delete row[key];
        pw[role] = row;
      }
      next.phase_weights = pw;
    }
    onChange(next);
  };

  const setPhasePct = (key: string, pct: number) =>
    patch({ phase_percentages: { ...draftConfig.phase_percentages, [key]: fromPct(pct) } });

  const sectionsForPhase = (key: string): string[] =>
    (draftConfig.phase_section_map ?? []).find((s: { phase_key: string }) => s.phase_key === key)
      ?.section_keys ?? [];

  const toggleSection = (phaseKey: string, sectionKey: string) => {
    const map = [...(draftConfig.phase_section_map ?? [])];
    let entry = map.find((s: { phase_key: string }) => s.phase_key === phaseKey);
    if (!entry) {
      entry = { phase_key: phaseKey, section_keys: [], subsections: [] };
      map.push(entry);
    }
    const has = entry.section_keys.includes(sectionKey);
    entry.section_keys = has
      ? entry.section_keys.filter((k: string) => k !== sectionKey)
      : [...entry.section_keys, sectionKey];
    patch({ phase_section_map: map.map((s) => ({ ...s })) });
  };

  const setRolePct = (roleId: string, pct: number) =>
    patch({ role_percentages: { ...draftConfig.role_percentages, [roleId]: fromPct(pct) } });

  const setRoleAlloc = (size: string, roleId: string, pct: number) =>
    patch({
      role_allocations: {
        ...draftConfig.role_allocations,
        [size]: { ...(draftConfig.role_allocations?.[size] ?? {}), [roleId]: fromPct(pct) },
      },
    });

  const setOverrides = (next: Array<{ role_id: string; section_keys: string[] }>) =>
    patch({ role_section_overrides: next });

  const addRoleOverride = () =>
    setOverrides([...roleOverrides, { role_id: roles[0]?.id ?? '', section_keys: [] }]);

  const removeRoleOverride = (idx: number) =>
    setOverrides(roleOverrides.filter((_, i) => i !== idx));

  const setOverrideRole = (idx: number, roleId: string) =>
    setOverrides(roleOverrides.map((o, i) => (i === idx ? { ...o, role_id: roleId } : o)));

  const toggleOverrideSection = (idx: number, sectionKey: string) =>
    setOverrides(
      roleOverrides.map((o, i) => {
        if (i !== idx) return o;
        const has = o.section_keys.includes(sectionKey);
        return {
          ...o,
          section_keys: has
            ? o.section_keys.filter((k) => k !== sectionKey)
            : [...o.section_keys, sectionKey],
        };
      })
    );

  const setPhaseWeight = (roleId: string, phaseKey: string, pct: number) =>
    patch({
      phase_weights: {
        ...draftConfig.phase_weights,
        [roleId]: { ...(draftConfig.phase_weights?.[roleId] ?? {}), [phaseKey]: fromPct(pct) },
      },
    });

  const setVelocity = (size: string, hours: number) =>
    patch({ team_velocity: { ...draftConfig.team_velocity, [size]: hours } });

  const setThresholdMaxHours = (size: string, maxHours: number) =>
    patch({
      project_size_thresholds: (draftConfig.project_size_thresholds ?? []).map(
        (t: { size: string; max_hours: number | null }) =>
          t.size === size ? { ...t, max_hours: maxHours } : t
      ),
    });

  const setDisplay = (field: 'show_timeline' | 'show_gantt', value: boolean) =>
    patch({ display: { ...(draftConfig.display ?? {}), [field]: value } });

  // ── Render helpers ──────────────────────────────────────────────────────────
  const pctInput = (value: number, onCommit: (n: number) => void, disabled = false) =>
    !isEditing ? (
      <span className="rmp-readvalue" style={{ width: '72px' }}>
        {value}%
      </span>
    ) : (
      <div className="rmp-pct">
        <NumberInput
          min={0}
          max={100}
          step={1}
          value={value}
          disabled={disabled}
          onChange={(e) => onCommit(parseFloat(e.target.value) || 0)}
          style={{ width: '72px' }}
        />
        <span className="rmp-pct__sign">%</span>
      </div>
    );

  // Velocity tier (hours/week): plain bold value in read mode, compact NumberInput in edit mode.
  const velocityTier = (caption: string, value: number, onCommit: (n: number) => void) => (
    <div className="rmp-tier" key={caption}>
      <span className="rmp-tier__caption">{caption}</span>
      {!isEditing ? (
        <span className="rmp-tier__value">{value} hrs / wk</span>
      ) : (
        <div className="rmp-tier__field">
          <NumberInput
            min={1}
            step={1}
            size="compact"
            thousandSeparator={false}
            secondaryText="hrs / wk"
            value={value}
            onChange={(e) => onCommit(parseFloat(e.target.value) || 0)}
          />
        </div>
      )}
    </div>
  );

  // Project-size threshold tier: the size's upper hour bound. The unbounded top
  // tier (max_hours === null) shows "No limit" and stays unbounded.
  const thresholdTier = (t: { size: string; max_hours: number | null }) => (
    <div className="rmp-tier" key={t.size}>
      <span className="rmp-tier__caption">{labelize(t.size)}</span>
      {t.max_hours === null ? (
        <span className="rmp-tier__value">No limit</span>
      ) : !isEditing ? (
        <span className="rmp-tier__value">≤ {t.max_hours.toLocaleString()} hrs</span>
      ) : (
        <div className="rmp-tier__field">
          <NumberInput
            min={1}
            step={1}
            size="compact"
            secondaryText="hrs max"
            value={t.max_hours}
            onChange={(e) => setThresholdMaxHours(t.size, parseFloat(e.target.value) || 0)}
          />
        </div>
      )}
    </div>
  );

  // Visibility setting row: label + description on the left, a token-styled toggle
  // switch (reusing the existing .ptp-est-range-config__pill-toggle) + On/Off caption
  // on the right. The switch is disabled (static) in read mode, interactive in edit.
  const toggleRow = (
    label: string,
    desc: string,
    checked: boolean,
    onCommit: (v: boolean) => void
  ) => (
    <div className="rmp-setting-row">
      <div className="rmp-setting-row__text">
        <span className="rmp-setting-row__label">{label}</span>
        <span className="rmp-setting-row__desc">{desc}</span>
      </div>
      <div className="rmp-setting-row__control">
        <label className="ptp-est-range-config__pill-toggle">
          <input
            type="checkbox"
            checked={checked}
            disabled={!isEditing}
            onChange={(e) => onCommit(e.target.checked)}
          />
          <span className="ptp-est-range-config__pill" />
        </label>
        <span className="rmp-toggle-caption">{checked ? 'On' : 'Off'}</span>
      </div>
    </div>
  );

  /**
   * Generic role × column percentage matrix, shared by role allocation
   * (columns = project sizes) and phase weights (columns = phases).
   * `colSums` adds a footer row of per-column totals (role allocation: each
   * size should total 100%); `rowSums` adds a trailing Σ column of per-row
   * totals (phase weights: each role's phases should total 100%).
   */
  const renderMatrix = (
    cols: Array<{ key: string; label: string }>,
    get: (roleId: string, colKey: string) => number | undefined,
    set: (roleId: string, colKey: string, pct: number) => void,
    opts: {
      colSums?: boolean;
      rowSums?: boolean;
      /** Marker text for a role whose hours are direct-assigned (else null). */
      roleBadge?: (roleId: string) => string | null;
      /** Disable a role's cells (e.g. direct-assigned roles ignore their %). */
      disableRole?: (roleId: string) => boolean;
    } = {}
  ) => {
    if (roles.length === 0 || cols.length === 0) {
      return <p className="rmp-hint">No roles defined. Add roles to the rate card first.</p>;
    }
    const colCount = cols.length + (opts.rowSums ? 1 : 0);
    return (
      <div className="rmp-matrix" style={{ ['--rmp-cols' as string]: colCount }}>
        <div className="rmp-matrix__head">
          <span className="rmp-matrix__corner" />
          {cols.map((c) => (
            <span key={c.key} className="rmp-matrix__col">
              {c.label}
            </span>
          ))}
          {opts.rowSums ? <span className="rmp-matrix__col">Σ</span> : null}
        </div>
        {roles.map((r) => {
          const badge = opts.roleBadge?.(r.id) ?? null;
          const disabled = opts.disableRole?.(r.id) ?? false;
          return (
            <div key={r.id} className="rmp-matrix__row">
              <Tooltip.Simple label={badge ?? null}>
                <span className="rmp-matrix__rowlabel">
                  {r.label}
                  {badge ? ' · direct' : ''}
                </span>
              </Tooltip.Simple>
              {cols.map((c) => (
                <span key={c.key} className="rmp-matrix__cell">
                  {pctInput(toPct(get(r.id, c.key)), (n) => set(r.id, c.key, n), disabled)}
                </span>
              ))}
              {opts.rowSums ? (
                <span className="rmp-matrix__sum">
                  {sumPct(cols.map((c) => get(r.id, c.key)))}%
                </span>
              ) : null}
            </div>
          );
        })}
        {opts.colSums ? (
          <div className="rmp-matrix__row rmp-matrix__foot">
            <span className="rmp-matrix__rowlabel">Sum</span>
            {cols.map((c) => (
              <span key={c.key} className="rmp-matrix__sum">
                {sumPct(roles.map((r) => get(r.id, c.key)))}%
              </span>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  // ── Tab panels ────────────────────────────────────────────────────────────
  // Phase structure list (rate-card style, no section pills): drag-to-reorder,
  // rename (key auto-derived from the name), add/remove, and - for
  // flat_percentage - the per-phase percentage. The key is never shown.
  const phaseStructureList = (withPct: boolean) => (
    <>
      <div className="rmp-rows rmp-rows--phases">
        {phases.map((p, idx) =>
          isEditing ? (
            <div
              key={p.key}
              className={`rmp-phase rmp-phase--edit${dragOverIdx === idx ? ' rmp-phase--drag-over' : ''}`}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                handlePhaseDragStart(idx);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                handlePhaseDragOver(idx);
              }}
              onDragLeave={handlePhaseDragLeave}
              onDrop={(e) => {
                e.preventDefault();
                handlePhaseDrop(idx);
              }}
            >
              <div className="rmp-phase__main">
                <span className="da-option-row__drag-handle" aria-hidden="true">
                  ⠿
                </span>
                <input
                  className="rmp-input"
                  value={p.label}
                  onChange={(e) => updatePhase(p.key, 'label', e.target.value)}
                  onBlur={() => syncPhaseKey(p.key)}
                  placeholder="Phase name"
                />
                {withPct
                  ? pctInput(toPct(draftConfig.phase_percentages?.[p.key]), (n) =>
                      setPhasePct(p.key, n)
                    )
                  : null}
                <button
                  type="button"
                  className="rmp-iconbtn rmp-iconbtn--danger"
                  onClick={() => removePhase(p.key)}
                  aria-label="Remove phase"
                >
                  ×
                </button>
              </div>
            </div>
          ) : (
            <div key={p.key} className="rmp-phase">
              <div className="rmp-phase__main">
                <span className="rmp-phase__order">{idx + 1}</span>
                <span className="rmp-phase__label">{p.label}</span>
                {withPct ? (
                  <>
                    <span className="rmp-phase__spacer" />
                    {pctInput(toPct(draftConfig.phase_percentages?.[p.key]), (n) =>
                      setPhasePct(p.key, n)
                    )}
                  </>
                ) : null}
              </div>
            </div>
          )
        )}
      </div>
      {withPct ? (
        <p className="rmp-hint">
          Phases sum to {sumPct(phases.map((p) => draftConfig.phase_percentages?.[p.key]))}% - the
          engine renormalizes proportionally.
        </p>
      ) : null}
      {isEditing ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={addPhase}
          style={{ marginTop: 'var(--space-3)' }}
        >
          + Add phase
        </Button>
      ) : null}
    </>
  );

  // Phase → estimate-section attachment (size_tiered "system calculation"):
  // read-only phase label + the section chip toggles. Phase structure itself is
  // owned by the Settings tab.
  const phaseSectionList = (
    <div className="rmp-rows rmp-rows--phases">
      {phases.map((p) => (
        <div key={p.key} className="rmp-phase">
          <div className="rmp-phase__main">
            <span className="rmp-phase__label">{p.label}</span>
          </div>
          <div className="rmp-chips">
            {SALES_ESTIMATE_SECTION_KEYS.map((sk) => {
              const on = sectionsForPhase(p.key).includes(sk);
              return (
                <button
                  key={sk}
                  type="button"
                  className={`rmp-chip${on ? ' rmp-chip--on' : ''}`}
                  disabled={!isEditing}
                  onClick={() => toggleSection(p.key, sk)}
                >
                  {labelize(sk)}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  // Section → role direct-assignment editor (size_tiered only): each entry sends
  // an estimate section's hours straight to a role, before the % allocation.
  const overridesEditor = (
    <section className="rmp-card">
      <div className="rmp-card__title">Direct section assignments</div>
      <p className="ptp-est-range-config__description">
        Send an estimate section’s hours straight to a role before the percentage split.
        Direct-assigned roles are excluded from the size allocation above.
      </p>
      {roleOverrides.length === 0 ? (
        <p className="rmp-hint">No direct assignments.</p>
      ) : (
        <div className="rmp-rows rmp-rows--phases">
          {roleOverrides.map((o, idx) => (
            <div key={idx} className="rmp-phase">
              <div className="rmp-phase__main">
                {isEditing ? (
                  <select
                    className="rmp-input"
                    value={o.role_id}
                    onChange={(e) => setOverrideRole(idx, e.target.value)}
                  >
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="rmp-phase__label">
                    {roles.find((r) => r.id === o.role_id)?.label ?? o.role_id}
                  </span>
                )}
                {isEditing ? (
                  <button
                    type="button"
                    className="rmp-iconbtn rmp-iconbtn--danger"
                    onClick={() => removeRoleOverride(idx)}
                    aria-label="Remove direct assignment"
                  >
                    ×
                  </button>
                ) : null}
              </div>
              <div className="rmp-chips">
                {SALES_ESTIMATE_SECTION_KEYS.map((sk) => {
                  const on = o.section_keys.includes(sk);
                  return (
                    <button
                      key={sk}
                      type="button"
                      className={`rmp-chip${on ? ' rmp-chip--on' : ''}`}
                      disabled={!isEditing}
                      onClick={() => toggleOverrideSection(idx, sk)}
                    >
                      {labelize(sk)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      {isEditing ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={addRoleOverride}
          disabled={roles.length === 0}
          style={{ marginTop: 'var(--space-3)' }}
        >
          + Add direct assignment
        </Button>
      ) : null}
    </section>
  );

  const rolesPanel =
    model === 'flat_percentage' ? (
      <div className="rmp-rows">
        {roles.length === 0 ? (
          <p className="rmp-hint">No roles defined. Add roles to the rate card first.</p>
        ) : (
          <>
            {roles.map((r) => (
              <div key={r.id} className="rmp-role">
                <span className="rmp-role__label">{r.label}</span>
                {pctInput(toPct(draftConfig.role_percentages?.[r.id]), (n) => setRolePct(r.id, n))}
              </div>
            ))}
            <p className="rmp-hint">
              Roles sum to {sumPct(roles.map((r) => draftConfig.role_percentages?.[r.id]))}%.
            </p>
          </>
        )}
      </div>
    ) : (
      <>
        {renderMatrix(
          sizes.map((s) => ({ key: s, label: labelize(s) })),
          (roleId, size) => draftConfig.role_allocations?.[size]?.[roleId],
          (roleId, size, pct) => setRoleAlloc(size, roleId, pct),
          {
            colSums: true,
            roleBadge: (roleId) => {
              const secs = overrideSectionsForRole(roleId);
              return secs.length ? `Direct: ${secs.map(labelize).join(', ')}` : null;
            },
            disableRole: (roleId) => overrideRoleIds.has(roleId),
          }
        )}
        {overrideRoleIds.size > 0 ? (
          <p className="rmp-hint">
            Columns may total under 100% - direct-assigned roles are excluded and the remaining
            roles are renormalized to 100%.
          </p>
        ) : null}
        {overridesEditor}
      </>
    );

  const effortPanel = renderMatrix(
    phases.map((p) => ({ key: p.key, label: p.label })),
    (roleId, phaseKey) => draftConfig.phase_weights?.[roleId]?.[phaseKey],
    (roleId, phaseKey, pct) => setPhaseWeight(roleId, phaseKey, pct),
    { rowSums: true }
  );

  const velocityHelper =
    draftConfig.description ||
    'Hours of delivery capacity assumed per week. Drives the project timeline length.';

  const settingsPanel = (
    <div className="rmp-settings">
      {/* Project size thresholds (size_tiered only) */}
      {model === 'size_tiered' ? (
        <section className="rmp-card">
          <div className="rmp-card__title">Project size thresholds</div>
          <p className="ptp-est-range-config__description">
            Total estimated hours map to a project size; velocity and the role mix are applied per
            size.
          </p>
          <div className="rmp-tier-group">
            {(draftConfig.project_size_thresholds ?? []).map(
              (t: { size: string; max_hours: number | null }) => thresholdTier(t)
            )}
          </div>
          {isEditing ? (
            <p className="rmp-hint">
              Boundaries must increase from the smallest to the largest tier.
            </p>
          ) : null}
        </section>
      ) : null}

      {/* Team velocity */}
      <section className="rmp-card">
        <div className="rmp-card__title">Team velocity (hours / week)</div>
        <p className="ptp-est-range-config__description">{velocityHelper}</p>
        <div className="rmp-tier-group">
          {model === 'size_tiered'
            ? sizes.map((size) =>
                velocityTier(labelize(size), draftConfig.team_velocity?.[size] ?? 0, (n) =>
                  setVelocity(size, n)
                )
              )
            : velocityTier('Hours per week', draftConfig.flat_velocity ?? 0, (n) =>
                patch({ flat_velocity: n })
              )}
        </div>
      </section>

      {/* Phases (size_tiered only) */}
      {model === 'size_tiered' ? (
        <section className="rmp-card">
          <div className="rmp-card__title">Phases</div>
          <p className="ptp-est-range-config__description">
            Ordered phases used to structure the plan. Hours are system-calculated from the sections
            attached on the Phases tab.
          </p>
          {phaseStructureList(false)}
        </section>
      ) : null}

      {/* Visibility */}
      <section className="rmp-card">
        <div className="rmp-card__title">Visibility</div>
        <div className="rmp-setting-rows">
          {toggleRow(
            'Show timeline',
            'Render the calculated phase timeline on the estimate.',
            draftConfig.display?.show_timeline ?? true,
            (v) => setDisplay('show_timeline', v)
          )}
          {toggleRow(
            'Show Gantt chart',
            'Include a Gantt view of phases and roles.',
            draftConfig.display?.show_gantt ?? true,
            (v) => setDisplay('show_gantt', v)
          )}
        </div>
      </section>
    </div>
  );

  // The `ai` model carries no static phases/velocity/role machinery - the AI
  // distributes hours per deal. Render a read-only explainer instead of the editor.
  if (model === 'ai') {
    return (
      <div className="rmp">
        <section className="rmp-card">
          <div className="rmp-card__title">AI-driven distribution</div>
          <p className="ptp-est-range-config__description">
            Hours are distributed across this category’s roles by AI for each deal, based on each
            role’s functional description and the deal’s scope. The Project Manager receives a fixed
            share (the calculator’s pm_percent). There are no static phases, velocity, or role
            percentages to configure here.
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="rmp">
      <Tabs
        className="rmp-tabs"
        variant="subnav"
        size="md"
        ariaLabel="Resource model sections"
        items={RM_TABS}
        value={tab}
        onValueChange={setTab}
      >
        <TabsContent value="settings">{settingsPanel}</TabsContent>
        <TabsContent value="phases">
          <p className="rmp-tab__desc">
            {model === 'size_tiered'
              ? 'Attach estimate sections to each phase - its hours are system-calculated from them. Phases are defined in Settings.'
              : 'Each phase receives a flat percentage of the total hours.'}
          </p>
          {model === 'size_tiered' ? phaseSectionList : phaseStructureList(true)}
        </TabsContent>
        <TabsContent value="roles">
          <p className="rmp-tab__desc">
            {model === 'flat_percentage'
              ? 'Share of total hours per role.'
              : 'Share of total hours per role, per project size.'}
          </p>
          {rolesPanel}
        </TabsContent>
        <TabsContent value="effort">
          <p className="rmp-tab__desc">
            How each role’s hours are distributed across phases. Each role should total 100%.
          </p>
          {effortPanel}
        </TabsContent>
      </Tabs>
    </div>
  );
}
